import React, { useState } from 'react';

import { Box, Button } from '@rbilabs/components-library/build/components';
import { useIntl } from 'react-intl';
import styled from 'styled-components';

import { ILocation } from '@rbi-ctg/frontend';
import { ActionButton, ActionButtonVariants } from 'components/action-button';
import { useMediaQuery } from 'hooks/use-media-query';
import { StoreLocatorSteps } from 'pages/store-locator/types';
import { useGeolocation } from 'state/geolocation';

import { DeliveryTerms } from '../delivery-terms';
import { UseCurrentLocationButton } from '../use-current-location-button';

import { LocationTermsModal } from './location-terms-modal';

const LoadingText = styled.p`
  margin: 0;
  font-weight: ${Styles.fontWeight.heavy};
  text-align: center;
  font-size: 0.9rem;
  line-height: 1.25rem;
  white-space: normal;
`;

const LocationContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
`;

const ShareTitle = styled.h1`
  width: 100%;
  white-space: initial;
  text-align: center;
  font-family: ${Styles.fontFamily.base};
  text-transform: ${Styles.textTransform.headlines};
  margin-block-start: 5rem;
  margin-block-end: 1rem;
  margin-inline: 0;
  font-size: 1.3rem;
`;

const LocationTermsButton = styled(Button)`
  background: none;
  border: none;
  color: ${Styles.color.tertiary};
  font-family: ${Styles.fontFamily.body};
  font-size: 0.875rem;
  font-weight: normal;
  text-decoration: underline;
  cursor: pointer;
  padding: 0;
  margin: 0;
  margin-block-start: 8px;

  &:hover {
    text-decoration: none;
    background-color: transparent;
  }
`;

const ShareDescription = styled.div`
  width: 100%;
  white-space: initial;
  text-align: center;
  font-family: ${Styles.fontFamily.body};
  margin-block-start: 0;
  margin-block-end: 2rem;
  margin-inline: 0;
  font-size: 0.95rem;
`;

const StyledActionButton = styled(ActionButton)`
  cursor: pointer;
`;

interface IDeliveryFormFooterProps {
  isDisabled: boolean;
  isLoading: boolean;
  coordinates: ILocation | undefined;
  onUseCurrentLocationClick: VoidFunction;
  onShareLocationClick: VoidFunction;
  matchCurrentStep: (step: StoreLocatorSteps) => boolean;
}

export const DeliveryAddressFormFooter: React.FC<IDeliveryFormFooterProps> = ({
  isLoading,
  isDisabled,
  coordinates,
  onShareLocationClick,
  onUseCurrentLocationClick,
  matchCurrentStep,
}) => {
  const { isPermissionGranted } = useGeolocation();
  const { formatMessage } = useIntl();
  const isDesktop = useMediaQuery('desktop');

  const [locationTermsOpen, setLocationTermsOpen] = useState(false);

  const shouldRenderDeliverBtn = matchCurrentStep(StoreLocatorSteps.CONFIRM_ADDRESS) && coordinates;
  const shouldRenderShareBtn =
    matchCurrentStep(StoreLocatorSteps.ADDRESS_SELECTOR) && !isPermissionGranted && !coordinates;
  const shouldRenderCurentLocation =
    matchCurrentStep(StoreLocatorSteps.ADDRESS_SELECTOR) && isPermissionGranted;

  return (
    <>
      <LocationTermsModal
        isOpen={locationTermsOpen}
        dismissModal={() => setLocationTermsOpen(false)}
      />

      {shouldRenderShareBtn && (
        <LocationContainer>
          <ShareTitle>{formatMessage({ id: 'deliveryShareLocationTitle' })}</ShareTitle>
          <ShareDescription>
            {formatMessage({ id: 'deliveryShareLocationDescription' })}
          </ShareDescription>
          <StyledActionButton data-testid="share-location-button" onClick={onShareLocationClick}>
            {formatMessage({ id: 'deliveryShareMyLocation' })}
          </StyledActionButton>

          <LocationTermsButton onClick={() => setLocationTermsOpen(true)}>
            {formatMessage({ id: 'deliveryLocationTerms' })}
          </LocationTermsButton>
        </LocationContainer>
      )}

      {shouldRenderCurentLocation && (
        <UseCurrentLocationButton onClick={onUseCurrentLocationClick} />
      )}

      {shouldRenderDeliverBtn && (
        <>
          <Box flexDirection="column" center margin="2rem 0 0">
            <Box width={isDesktop ? '50%' : '100%'} center margin="0 0 1rem">
              <ActionButton
                type="submit"
                fullWidth
                disabled={isLoading || isDisabled}
                isLoading={isLoading}
                data-testid="deliver-here"
                variant={ActionButtonVariants.PRIMARY}
              >
                {formatMessage({ id: 'deliverHere' })}
              </ActionButton>
            </Box>
          </Box>
          <DeliveryTerms />
        </>
      )}

      {isLoading && (
        <Box center>
          <LoadingText>{formatMessage({ id: 'deliveryCheckingMessage' })}</LoadingText>
        </Box>
      )}
    </>
  );
};
